import { supabase } from "@/auth/supabase-client";
import { ensureFreshSession, withSupabaseTimeout } from "@/auth/session-guard";
import type {
  ConsultantRosterActivation,
  ConsultantRosterPreview,
  ConsultantRosterRecord,
} from "./types";

const FUNCTION_NAME = "import_assignable_consultants";
const IMPORT_TIMEOUT_MS = 60_000;

async function messageFor(error: unknown): Promise<string> {
  const context = (error as { context?: Response }).context;
  if (context && typeof context.json === "function") {
    try {
      const parsed = (await context.json()) as {
        error?: { message?: string };
        message?: string;
      };
      if (typeof parsed.error?.message === "string") return parsed.error.message;
      if (typeof parsed.message === "string") return parsed.message;
    } catch {
      // Non-JSON failures fall through to the provider message.
    }
  }
  return error instanceof Error ? error.message : String(error);
}

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke(FUNCTION_NAME, { body }),
    "Consultant roster request",
    IMPORT_TIMEOUT_MS
  );
  if (error) throw new Error(await messageFor(error));
  const envelope = data as { data?: T } | null;
  if (!envelope?.data) throw new Error("Consultant roster response was malformed.");
  return envelope.data;
}

export function previewConsultantRoster(
  records: ConsultantRosterRecord[]
): Promise<ConsultantRosterPreview> {
  return invoke({ mode: "preview", records });
}

export function activateConsultantRosterBatch(
  records: ConsultantRosterRecord[],
  digest: string,
  startOrdinal: number,
  batchSize = 20
): Promise<ConsultantRosterActivation> {
  return invoke({
    mode: "activate",
    records,
    confirmation_digest: digest,
    start_ordinal: startOrdinal,
    batch_size: batchSize,
  });
}
